import { Component, inject, Input, input, OnChanges, OnDestroy, OnInit, output, SimpleChanges } from '@angular/core';
import { ZiroFormConfig, ZiroFormsMessage, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import {
  EndUser,
  Global360View,
  Voicemail,
  VoicemailAlternateExtension,
  VoicemailFieldConfig,
  VoicemailRef,
} from '../../../../shared/models/generated/ziroModels';
import { VoicemailSearchResource } from '../../../../shared/resources/voicemail-search.resource';
import { forkJoin, Observable, of, Subscriber, Subscription, throwError } from 'rxjs';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { catchError, delayWhen, map, tap } from 'rxjs/operators';
import { ZiroSelectConfig } from '../../../../forms/fields/select/ziro-select.component';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { VoicemailResource } from '../../../../shared/resources/voicemail.resource';
import { DefaultEndUserResource } from '../../../../shared/resources/default-end-user.resource';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { VoicemailAlternateExtensionsResource } from '../../../../shared/resources/voicemail-alternate-extensions.resource';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../../shared/bottom-nav/bottom-nav.service';
import { ActivatedRoute, Router } from '@angular/router';
import { VoicemailSmtpNotificationsDeviceResource } from '../../../../shared/resources/voicemail-smtp-notifications-device.resource';
import { PhoneButtonsService } from '../../../../shared/phone-buttons/shared/phone-buttons.service';
import { VoicemailService, VoicemailServiceParams } from '../../../../shared/services/voicemail.service';
import { isEqual } from 'lodash';
import { ZiroMultiTextConfig } from '../../../../forms/fields/multi-text/ziro-multi-text.component';

export interface VoicemailFormEntity {
  alias: string;
  voicemailTemplate: string;
  firstName: string;
  lastName: string;
  displayName: string;
  alternateExtensions: string[];
  smtpNotificationDevice: string;
}

@Component({
  selector: 'ziro-voicemail-form',
  templateUrl: './voicemail-form.component.html',
  providers: [VoicemailService],
  standalone: false,
})
export class VoicemailFormComponent
  extends ZiroFormAbstractDirective<VoicemailFormEntity, VoicemailFormEntity>
  implements OnInit, OnDestroy, OnChanges
{
  private route = inject(ActivatedRoute);
  private voicemailSearchResource = inject(VoicemailSearchResource);
  private ziroModalService = inject(ZiroModalService);
  private translateService = inject(TranslateService);
  private toastService = inject(ToastService);
  private voicemailResource = inject(VoicemailResource);
  private defaultEndUserResource = inject(DefaultEndUserResource);
  private voicemailAlternateExtensionsResource = inject(VoicemailAlternateExtensionsResource);
  private bottomNavService = inject(BottomNavService);
  protected _ziroFormStateService: ZiroFormStateService;
  private router = inject(Router);
  private voicemailSmtpNotificationsDeviceResource = inject(VoicemailSmtpNotificationsDeviceResource);
  private phoneButtonsService = inject(PhoneButtonsService);
  private voicemailService = inject(VoicemailService);

  readonly modalProperties = input<any>();
  readonly fieldConfig = input<VoicemailFieldConfig>();
  readonly endUser = input<EndUser>();
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() isExistingVoicemail: boolean;
  readonly isExistingVoicemailOnPublicPhone = input<boolean>();
  readonly unityServerId = input<number>();
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() isPending = false;
  readonly serverId = input<number>();
  readonly siteId = input<number>();
  readonly extension = input<string>();
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() voicemail: Voicemail;
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() originalValue: VoicemailFormEntity;
  readonly username = input<string>();
  readonly alternateExtensions = input<VoicemailAlternateExtension[]>([]);
  readonly global360View = input<Global360View>();

  readonly voicemailDeleted = output<boolean | string>();
  readonly voicemailSaved = output<boolean | string>();
  readonly voicemailRefUpdated = output<VoicemailRef>();

  ALTERNATE_EXTENSION_MAX_LENGTH_NUMBER = 40;
  DISPLAY_NAME_MAX_LENGTH_NUMBER = 64;
  fieldGroups = {
    textInputs: ['alias', 'firstName', 'lastName', 'displayName'],
  };
  ziroIcons = ZiroIcons;
  buttonStyles = ButtonStyles;
  voicemailId: string;
  isDeleting = false;
  isSaving = false;
  voicemailRef: VoicemailRef;

  protected formConfig: ZiroFormConfig;
  private _validators = {
    aliasPatternValidator: (value: string) =>
      !value || /^[^\s()[\]\\,<>:;]{1,64}$/.test(value)
        ? ZiroFormsValidationState.VALID
        : ZiroFormsValidationState.INVALID,
    voiceMailAliasValidator: (value: string) => {
      if (!value) {
        return ZiroFormsValidationState.VALID;
      }
      return this.isVoiceMailAliasValid(value);
    },
    alternateExtensionsPatternValidator: (values: string[]) =>
      values.every((value: string) =>
        /(^[+*#0-9]*$)|(^(sip:)?[-._0-9A-Za-z]+@(([0-9]+\.[0-9]+\.[0-9]+\.[0-9]+)|([0-9A-Za-z][-0-9A-Za-z]*(\.[-0-9A-Za-z]+)*\.[A-Za-z](([-0-9A-Za-z]*[0-9A-Za-z])|([0-9A-Za-z]*))))$)/.test(
          value,
        ),
      )
        ? ZiroFormsValidationState.VALID
        : ZiroFormsValidationState.INVALID,
    alternateExtensionValueValidator: (values: string[]) =>
      values.every((value: string) => {
        const endUser = this.endUser();
        return !(endUser && value === endUser.primaryExtension.extension);
      })
        ? ZiroFormsValidationState.VALID
        : ZiroFormsValidationState.INVALID,
    alternateExtensionsLengthValidator: (values: string[]) =>
      values.every((value: string) => value.length <= this.ALTERNATE_EXTENSION_MAX_LENGTH_NUMBER)
        ? ZiroFormsValidationState.VALID
        : ZiroFormsValidationState.INVALID,
    displayNameLengthValidator: (value: string) =>
      value.length <= this.DISPLAY_NAME_MAX_LENGTH_NUMBER
        ? ZiroFormsValidationState.VALID
        : ZiroFormsValidationState.INVALID,

    smtpNotificationDevicePatternValidator: (value: string) =>
      !value ||
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        value,
      )
        ? ZiroFormsValidationState.VALID
        : ZiroFormsValidationState.INVALID,
  };
  private _duplicateExtension: string;
  private _duplicateAlias: string;
  private _alternateExtensions: VoicemailAlternateExtension[];
  private _subs = new Subscription();

  ngOnInit(): void {
    this._initVoiceMailFormConfig();
    if (!this.modalProperties()) {
      this._initBottomNav();
    }

    const ziroFormsSub = this.ziroFormsUpdate$.subscribe((update) => {
      if (update.valid === ZiroFormsValidationState.VALID) {
        this.bottomNavService.setBottomNavValidationError(false);
      }
    });
    this._subs.add(ziroFormsSub);

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() => {
      this._setPending(true);
      this.isErrorPresent.subscribe((hasError: boolean) => {
        if (hasError) {
          this._setPending(false);
        }
        this.bottomNavService.dispatch(
          new BottomNavUpdateState({
            hasValidationError: hasError,
          }),
        );
      });
    });
    this._subs.add(formSubmittedSub);

    this._alternateExtensions = this.entity.alternateExtensions.map((altExt) => {
      const existingExtension = this.alternateExtensions().find((existingExtension) => {
        return altExt === existingExtension.extension;
      });
      return existingExtension ? { ...existingExtension } : { id: null, extension: altExt };
    });
    const formUpdateSub = this.ziroFormsUpdate$.subscribe((update) => {
      if (
        update.old?.alternateExtensions &&
        update.new?.alternateExtensions &&
        !isEqual(update.new.alternateExtensions, update.old.alternateExtensions)
      ) {
        const extensionWasAdded = update.new.alternateExtensions.length > update.old.alternateExtensions.length;
        const extensionWasRemoved = update.new.alternateExtensions.length < update.old.alternateExtensions.length;
        if (extensionWasAdded) {
          const newExt = update.new.alternateExtensions[update.new.alternateExtensions.length - 1];
          this._alternateExtensions.push({ id: null, extension: newExt });
        } else if (extensionWasRemoved) {
          this._alternateExtensions = this._alternateExtensions.filter((ext) =>
            update.new.alternateExtensions.includes(ext.extension),
          );
        } else {
          for (let i = 0; i < update.new.alternateExtensions.length; i++) {
            if (update.new.alternateExtensions[i] !== update.old.alternateExtensions[i]) {
              this._alternateExtensions[i].extension = update.new.alternateExtensions[i];
              break;
            }
          }
        }
      }
    });
    this._subs.add(formUpdateSub);
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes.isPending) {
      const isPending = changes.isPending.currentValue as boolean;
      this._validateAndSubmitSource.next(isPending);
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subs.unsubscribe();
  }

  isVoiceMailAliasValid(alias: string): Observable<ZiroFormsValidationState> {
    return this.voicemailSearchResource.searchByAlias(alias, this.unityServerId()).pipe(
      map((results) =>
        results.every((user) => {
          if (
            alias !== user.ref.alias ||
            (this.isExistingVoicemail && this.voicemail.alias === user.ref.alias) ||
            (this.isExistingVoicemailOnPublicPhone() && this.voicemail.alias === user.ref.alias)
          ) {
            return true;
          } else {
            this._duplicateAlias = user.ref.alias;
            this._duplicateExtension = user.ref.extension;
            return false;
          }
        })
          ? ZiroFormsValidationState.VALID
          : ZiroFormsValidationState.INVALID,
      ),
    );
  }

  handleDeleteClick(): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: encodeURIComponent('text-danger'),
        promptBody: this.translateService.instant('tkey;search.voicemail.delete.warning.text', {
          alias: this.originalValue.alias,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._onDeleteConfirm(),
          },
        ],
      },
    };

    this.ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  handleSaveClick(): Observable<VoicemailRef | void> {
    this.isPending = true;
    this._setPending(this.isPending);
    return this._updateVoicemail().pipe(
      delayWhen(() =>
        forkJoin([this._addOrDeleteAlternateExtensionsIfNeeded(), this._saveSmtpNotificationDeviceIfNeeded()]),
      ),
      delayWhen(() =>
        forkJoin([this._callDefaultDirectoryNumberResourceIfNeeded(), this._callDefaultUserResourceIfNeeded()]),
      ),
      tap(() => {
        this.originalValue = { ...this.formData };
        this.isExistingVoicemail = true;
        this.isPending = false;
        this._ziroFormStateService.setIsFormDirty(false);
        if (!this.modalProperties()) {
          this.voicemailSaved.emit(true);
        } else {
          this.voicemailSaved.emit(this.formData.alias);
        }
        this._showSuccessSaveToast();
      }),
      catchError((response) => {
        this.isSaving = false;
        this.isPending = false;
        this._setPending(this.isPending);
        return throwError(() => response);
      }),
    );
  }

  submit(): Observable<VoicemailRef | void> {
    return this.handleSaveClick();
  }

  private _initVoiceMailFormConfig(): void {
    this.formConfig = {
      fields: {
        alias: {
          fieldId: 'alias',
          label: 'tkey;shared.voicemail.alias.label',
          dataAutomation: 'voicemail-alias',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          validation: [
            {
              validator: this._validators.aliasPatternValidator,
              message: 'tkey;validators.global.error.pattern',
            },
            {
              validator: this._validators.voiceMailAliasValidator,
              message: (): ZiroFormsMessage => {
                return {
                  content: 'tkey;addvoicemail.alias.validation.error.helpblock',
                  params: {
                    alias: this._duplicateAlias,
                    dn: this._duplicateExtension,
                  },
                };
              },
            },
          ],
          hidden: () => !this.fieldConfig().alias.show,
          required: () => this.fieldConfig().alias.required,
          defaultValue: () => this.fieldConfig().alias.defaultValue,
        },
        firstName: {
          fieldId: 'firstName',
          label: 'tkey;shared.voicemail.firstname.label',
          dataAutomation: 'voicemail-first-name',
          hidden: () => !this.fieldConfig().firstName.show,
          required: () => this.fieldConfig().firstName.required,
          defaultValue: () => this.fieldConfig().firstName.defaultValue,
        },
        lastName: {
          fieldId: 'lastName',
          label: 'tkey;shared.voicemail.lastname.label',
          dataAutomation: 'voicemail-last-name',
          hidden: () => !this.fieldConfig().lastName.show,
          required: () => this.fieldConfig().lastName.required,
          defaultValue: () => this.fieldConfig().lastName.defaultValue,
        },
        voicemailTemplate: {
          fieldId: 'voicemailTemplate',
          label: 'tkey;site_management.site.section.field.voicemail_template.text',
          dataAutomation: 'voicemail-template',
          componentConfig: new ZiroSelectConfig({ options: this.fieldConfig().voicemailTemplate.possibleOptions }),

          hidden: () => this.isExistingVoicemail || !this.fieldConfig().voicemailTemplate.show,
          required: () => this.fieldConfig().voicemailTemplate.required,
          defaultValue: () => this.fieldConfig().voicemailTemplate.defaultValue,
        },
        displayName: {
          fieldId: 'displayName',
          label: 'tkey;shared.voicemail.displayname.label',
          dataAutomation: 'voicemail-display-Name',
          validation: [
            {
              validator: this._validators.displayNameLengthValidator,
              message: (): ZiroFormsMessage => {
                return {
                  content: 'tkey;validators.global.error.maxlength',
                  params: {
                    maxlength: this.DISPLAY_NAME_MAX_LENGTH_NUMBER,
                  },
                };
              },
            },
          ],
          hidden: () => !this.fieldConfig().displayName.show,
          required: () => this.fieldConfig().displayName.required,
          defaultValue: () => this.fieldConfig().displayName.defaultValue,
        },
        alternateExtensions: {
          fieldId: 'alternateExtensions',
          label: 'tkey;shared.voicemail.alternate_extensions.label',
          dataAutomation: 'voicemail-alternate-extensions',
          componentConfig: new ZiroMultiTextConfig({
            isFirstOptionDisabledWithTooltip: this._shouldDisableFirstAlternateExtension()
              ? 'tkey;shared.voicemail.alternate_extensions.disabled_mailid'
              : undefined,
          }),
          validation: [
            {
              validator: this._validators.alternateExtensionsPatternValidator,
              message: 'tkey;validators.global.error.pattern',
            },
            {
              validator: this._validators.alternateExtensionValueValidator,
              message: 'tkey;shared.voicemail.alternate_extensions.primary_extension_validation_error',
            },
            {
              validator: this._validators.alternateExtensionsLengthValidator,
              message: (): ZiroFormsMessage => {
                return {
                  content: 'tkey;validators.global.error.maxlength',
                  params: {
                    maxlength: this.ALTERNATE_EXTENSION_MAX_LENGTH_NUMBER,
                  },
                };
              },
            },
          ],
          hidden: () => !this.fieldConfig().alternateExtension.show,
          required: () => false,
        },
        smtpNotificationDevice: {
          fieldId: 'smtpNotificationDevice',
          label: 'tkey;site_management.site.section.field.smtp_notification_device.text',
          dataAutomation: 'voicemail-smtp-notification-device',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          validation: [
            {
              validator: this._validators.smtpNotificationDevicePatternValidator,
              message: 'tkey;validators.global.email.invalid.error',
            },
          ],
          hidden: () => (this.modalProperties() ? true : !this.fieldConfig().smtpNotificationDevice.show),
          required: () => this.fieldConfig().smtpNotificationDevice.required,
          defaultValue: () => this.fieldConfig().smtpNotificationDevice.defaultValue,
          valExcluded: () => !this.fieldConfig().smtpNotificationDevice.show,
        },
      },
    } as ZiroFormConfig;
  }

  private _shouldDisableFirstAlternateExtension(): boolean {
    return (
      this.endUser()?.mailId &&
      this.alternateExtensions() &&
      this.alternateExtensions()[0]?.extension &&
      this.endUser().mailId === this.alternateExtensions()[0].extension
    );
  }

  private _initBottomNav(): void {
    const bottomNavButtons = [];
    bottomNavButtons.push({
      id: 'cancel',
      dataAutomation: 'user-voicemail-cancel',
      label: 'tkey;global.button.cancel.text',
      buttonClass: ButtonStyles.DEFAULT,
      cb: () => this._goToUserHome(),
    });
    if (this.isExistingVoicemail) {
      bottomNavButtons.push({
        id: 'delete',
        dataAutomation: 'user-voicemail-delete',
        label: 'tkey;global.button.delete.text',
        icon: this.ziroIcons.DELETE_SMALL,
        buttonClass: ButtonStyles.DANGER,
        state: {
          pending: false,
          buttonDisableState: { disabled: !this.isExistingVoicemail, tooltipKey: '' },
        },
        cb: () => this.handleDeleteClick(),
      });
    }
    bottomNavButtons.push({
      id: 'save',
      dataAutomation: 'user-voicemail-save',
      label: 'tkey;global.button.save.text',
      icon: this.ziroIcons.SAVE_SUCCESS,
      buttonClass: ButtonStyles.PRIMARY,
      state: {
        pending: false,
        buttonDisableState: { disabled: this.isPending, tooltipKey: '' },
      },
      type: ButtonTypes.SUBMIT,
      submitSubject: this._validateAndSubmitSource,
    });
    this.bottomNavService.dispatch(new BottomNavUpdateButtonsList(bottomNavButtons));
  }

  private _goToUserHome(): void {
    this.router.navigate(this.voicemail ? ['../../'] : ['../'], {
      relativeTo: this.route,
    });
  }

  private _updateVoicemail = (): Observable<VoicemailRef | void> => {
    return new Observable<VoicemailRef | void>((subscriber) => {
      const isExistingVoicemailOnPublicPhone = this.isExistingVoicemailOnPublicPhone();
      if (this._wasAnyChangeDoneToVoicemail() || !this.isExistingVoicemail || !isExistingVoicemailOnPublicPhone) {
        if (this.isExistingVoicemail || isExistingVoicemailOnPublicPhone) {
          return this._updateExistingVoicemail().subscribe((voicemailRef) => {
            this.voicemailRefUpdated.emit(voicemailRef);
            subscriber.next(voicemailRef);
            subscriber.complete();
          });
        } else {
          return this._createNewVoicemail().subscribe((voicemailRef) => {
            this.voicemailRefUpdated.emit(voicemailRef);
            subscriber.next(voicemailRef);
            subscriber.complete();
          });
        }
      } else {
        subscriber.next();
        subscriber.complete();
      }
    });
  };

  private _addOrDeleteAlternateExtensionsIfNeeded = (): Observable<void> => {
    const observables: Observable<void>[] = [of(null)];

    // add new extensions
    const addAlternateExtensions$ = this._alternateExtensions
      .filter((ext) => !ext.id)
      .map((ext) =>
        this.voicemailAlternateExtensionsResource
          .post(this.unityServerId(), this.voicemail.id, ext.extension, this.formData.alias)
          .pipe(
            map(() => {
              this.toastService.pushSaveToast(
                'tkey;shared.voicemail.alternate_extensions.toast',
                ext.extension,
                ZiroIcons.VOICEMAIL_SMALL,
              );
            }),
          ),
      );
    observables.push(...addAlternateExtensions$);

    // delete old extensions
    const deleteAlternateExtensions$ = this.alternateExtensions()
      .filter((ext) => !this._alternateExtensions.find((newExt) => ext.id && ext.id === newExt.id))
      .map((ext) =>
        this.voicemailAlternateExtensionsResource
          .delete(this.unityServerId(), this.voicemail.id, ext.id, this.formData.alias)
          .pipe(
            map(() => {
              this.toastService.pushDeleteToast('tkey;shared.voicemail.alternate_extensions.toast', ext.extension);
            }),
          ),
      );
    observables.push(...deleteAlternateExtensions$);

    // modify changed extensions
    const modifyAlternateExtensions$ = this._alternateExtensions
      .filter(
        (ext) =>
          !!ext.id &&
          this.alternateExtensions().find((existingExtension) => existingExtension.id === ext.id)?.extension !==
            ext.extension,
      )
      .map((ext) =>
        this.voicemailAlternateExtensionsResource
          .put(this.unityServerId(), this.voicemail.id, ext.extension, ext.id, this.formData.alias)
          .pipe(
            map(() => {
              this.toastService.pushSaveToast(
                'tkey;shared.voicemail.alternate_extensions.toast',
                ext.extension,
                ZiroIcons.VOICEMAIL_SMALL,
              );
            }),
          ),
      );
    observables.push(...modifyAlternateExtensions$);

    return forkJoin(observables).pipe(map(() => {}));
  };

  private _saveSmtpNotificationDeviceIfNeeded(): Observable<void> {
    if (!this.endUser()) {
      // public phones and secondary lines do not have smtp notification device
      return of(null);
    }
    return new Observable<void>((subscriber: Subscriber<void>) => {
      if (
        !this.isExistingVoicemail ||
        this.originalValue.smtpNotificationDevice !== this.formData.smtpNotificationDevice
      ) {
        this.voicemailSmtpNotificationsDeviceResource
          .put(
            this.unityServerId(),
            this.voicemail.id,
            {
              email: this.formData.smtpNotificationDevice,
            },
            [{ name: 'Alias', value: this.formData.alias }],
          )
          .subscribe(() => {
            subscriber.next();
            subscriber.complete();
          });
      } else {
        subscriber.next();
        subscriber.complete();
      }
    });
  }

  private _callDefaultDirectoryNumberResourceIfNeeded = (): Observable<void> => {
    return new Observable<void>((subscriber: Subscriber<void>) => {
      if (!this.isExistingVoicemail) {
        const endUser = this.endUser();
        const modalProperties = this.modalProperties();
        this.voicemailService
          .setDnForwardToVoicemail(
            false,
            endUser?.username,
            modalProperties
              ? {
                  id: modalProperties.dnRef.id,
                  serverId: modalProperties.dnRef.serverId,
                  extension: modalProperties.dnRef.extension,
                }
              : {
                  id: endUser.primaryExtension.id,
                  serverId: endUser.primaryExtension.serverId,
                  extension: endUser.primaryExtension.extension,
                },
            this.siteId(),
          )
          .subscribe(() => {
            subscriber.next();
            subscriber.complete();
          });
      } else {
        subscriber.next();
        subscriber.complete();
      }
    });
  };

  private _callDefaultUserResourceIfNeeded = (): Observable<void> => {
    return new Observable<void>((subscription: Subscriber<void>) => {
      const endUser = this.endUser();
      const modalProperties = this.modalProperties();
      if ((endUser && modalProperties?.lineButtonSummary.isPrimaryExtension) || (endUser && !modalProperties)) {
        this.defaultEndUserResource
          .post({
            username: this.username(),
            siteId: this.siteId().toString(),
            hasVoicemail: true,
            hasSnr: true,
            hasExtensionMobility: true,
          })
          .subscribe((defaultEndUser) => {
            this.voicemailService
              .setHomeCluster(this.global360View().endUsers, this.endUser().id, defaultEndUser.ucServiceProfile)
              .subscribe(() => {
                subscription.next();
                subscription.complete();
              });
          });
      } else {
        subscription.next();
        subscription.complete();
      }
    });
  };

  private _updateExistingVoicemail = (): Observable<VoicemailRef> => {
    this.voicemail.alias = this.formData.alias;
    this.voicemail.displayName = this.formData.displayName;
    this.voicemail.firstName = this.formData.firstName;
    this.voicemail.lastName = this.formData.lastName;
    return this.voicemailResource.put(this.unityServerId(), this.voicemail.id, this.voicemail);
  };

  private _createNewVoicemail = (): Observable<VoicemailRef> => {
    const endUser = this.endUser();
    return this.voicemailResource
      .post(
        this.unityServerId(),
        {
          id: null,
          alias: this.formData.alias,
          firstName: this.formData.firstName,
          lastName: this.formData.lastName,
          displayName: this.formData.displayName,
          extension: this.extension(),
          template: this.formData.voicemailTemplate,
        },
        endUser ? [{ name: 'username', value: endUser.username }] : null,
      )
      .pipe(
        tap((voicemailRef) => {
          this.voicemailRef = voicemailRef;
          this.voicemailRefUpdated.emit(voicemailRef);
        }),
        delayWhen((voicemailRef) => this._getVoicemailAndSetEntitySource(voicemailRef)),
      );
  };

  private _getVoicemailAndSetEntitySource(voicemailRef: VoicemailRef): Observable<Voicemail> {
    return this.voicemailResource.get(voicemailRef.serverId, voicemailRef.id).pipe(
      tap((voicemail) => {
        this.voicemail = { ...voicemail };
        this.voicemailId = voicemail.id;
        const modalProperties = this.modalProperties();
        if (modalProperties) {
          modalProperties.lineButtonSummary = {
            ...modalProperties.lineButtonSummary,
            voicemail: this.voicemail,
          };
        }
        this.phoneButtonsService.isVoicemailDeleted.next(false);
        this._setEntitySourceFromVoicemail();
      }),
    );
  }

  private _wasAnyChangeDoneToVoicemail = (): boolean => {
    return (
      this.originalValue.lastName !== this.formData.lastName ||
      this.originalValue.firstName !== this.formData.firstName ||
      this.originalValue.alias !== this.formData.alias ||
      this.originalValue.displayName !== this.formData.displayName ||
      this.originalValue.smtpNotificationDevice !== this.formData.smtpNotificationDevice ||
      this.originalValue.alternateExtensions !== this.formData.alternateExtensions
    );
  };

  private _setEntitySourceFromVoicemail: () => void = () => {
    this.originalValue.lastName = this.voicemail.lastName;
    this.originalValue.firstName = this.voicemail.firstName;
    this.originalValue.alias = this.voicemail.alias;
    this.originalValue.displayName = this.voicemail.displayName;
  };

  private _showSuccessSaveToast(): void {
    this.toastService.push(
      ToastTypes.SUCCESS,
      this.ziroIcons.VOICEMAIL_SMALL,
      'tkey;shared.toast.save.success.title',
      'tkey;userdetail.voicemail.save.toast.success.text',
      { alias: this.formData.alias },
    );
  }

  private _onDeleteConfirm(): Observable<boolean> {
    this.isDeleting = true;
    this._setPending(this.isDeleting);
    return new Observable((subscriber) => {
      const endUser = this.endUser();
      const global360View = this.global360View();
      const global360ViewValue = this.global360View();
      const serviceParams: VoicemailServiceParams = {
        endUserResult: global360View
          ? global360View.endUsers.find((endUserResult) => endUserResult.ref.id === this.endUser()?.id)
          : undefined,
        endUsers: global360ViewValue ? global360ViewValue.endUsers : undefined,
        unityServerId: this.unityServerId(),
        siteId: this.siteId(),
        dnRef: endUser ? endUser.primaryExtension : undefined,
        hasSnr: true,
        hasExtensionMobility: true,
      };

      const endUserValue = this.endUser();
      this.voicemailService
        .deprovisionVoicemail(
          this.voicemail.id,
          serviceParams,
          !!endUserValue && endUserValue.primaryExtension?.extension === this.voicemail.extension,
          false,
        )
        .subscribe({
          next: () => {
            this._onSuccessfulVoicemailDelete();
            subscriber.next(true);
          },
          error: () => {
            this.isDeleting = false;
            this._setPending(this.isDeleting);
            subscriber.next(false);
          },
        });
    });
  }

  private _showSuccessDeleteToast(): void {
    this.toastService.push(
      ToastTypes.INFO,
      `${this.ziroIcons.DELETE_SMALL} text-danger`,
      'tkey;search.voicemail.delete.success.text',
      'tkey;userdetail.voicemail.save.toast.success.text',
      { alias: this.formData.alias },
    );
  }

  private _setPending(state: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'cancel',
        state: {
          buttonDisableState: { disabled: state || this.isDeleting, tooltipKey: '' },
        },
      }),
    );
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'save',
        state: {
          pending: this.isDeleting ? false : state,
          buttonDisableState: { disabled: state, tooltipKey: '' },
        },
      }),
    );
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'delete',
        state: {
          pending: this.isDeleting,
          buttonDisableState: { disabled: this.isDeleting || state, tooltipKey: '' },
        },
      }),
    );
  }

  private _onSuccessfulVoicemailDelete(): void {
    this._showSuccessDeleteToast();
    this.voicemailDeleted.emit(true);
    this.voicemailDeleted.emit(this.voicemail.id);
    this.phoneButtonsService.isVoicemailDeleted.next(true);
    const modalProperties = this.modalProperties();
    if (modalProperties) {
      modalProperties.lineButtonSummary = {
        ...modalProperties.lineButtonSummary,
        voicemail: null,
      };
    }
    this.phoneButtonsService.lineButtonSummary.next(modalProperties?.lineButtonSummary);
    this._ziroFormStateService.setIsFormDirty(false);
  }
}
